<template>
  <div>
    <v-container fluid>
      <base-material-card
        icon="mdi-calculator-variant"
        title="ประมวลผลการเลื่อนค่าตอบแทนพนักงานราชการ (ส่วนกลาง)"
        class="px-5 py-3"
      >
        <v-alert
          type="info"
          outlined
          border="left"
          colored-border
          elevation="1"
          icon="mdi-information-outline"
          class="mb-4"
        >
          <div class="font-weight-bold">
            ส่วนกลางประมวลผลแทนสถานศึกษาทีละแห่งแบบอัตโนมัติ
            ด้วยหลักการเดียวกับเมนู "คำนวณค่าตอบแทน" ของสถานศึกษา
          </div>
          <div class="caption mt-1">
            วงเงินรวมไม่เกิน <strong>4%</strong> ของฐานค่าตอบแทน ×
            12 เดือน (ข้อ 1(3)) · รายบุคคลไม่เกิน <strong>6%</strong> (ข้อ
            1(2)) · ปัดเศษไม่ถึง 10 บาทเป็น 10 บาท (ข้อ 1(6)) · มีผล
            <strong>1 ตุลาคม</strong> · ผลที่ได้เป็น snapshot
            ณ เวลาประมวลผล ไม่แก้ไขค่าตอบแทนในทะเบียนประวัติ
          </div>
        </v-alert>

        <!-- ===== STEP 1: ปี + รอบการประมวลผล ===== -->
        <v-card outlined class="mb-4">
          <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-2">
            <v-avatar color="info darken-3" size="26" class="mr-2">
              <span class="white--text caption font-weight-bold">1</span>
            </v-avatar>
            เลือกปีและรอบการประมวลผล
          </v-card-title>
          <v-card-text class="pt-4">
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="compYear"
                  :items="years"
                  label="ปีงบประมาณที่ประเมิน"
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar-range"
                  :hint="`เลื่อนค่าตอบแทน 1 ต.ค. ${compYear}`"
                  persistent-hint
                  @change="loadOverview"
                />
              </v-col>
              <v-col cols="12" md="9">
                <div v-if="overview" class="d-flex flex-wrap" style="gap:8px">
                  <v-chip small outlined color="primary">
                    <v-icon small left>mdi-account-group</v-icon>
                    พนักงานราชการ
                    {{ fmt(overview.population.personnel) }} คน /
                    {{ fmt(overview.population.colleges) }} แห่ง
                  </v-chip>
                  <v-chip
                    v-for="r in [1, 2]"
                    :key="r"
                    small
                    outlined
                    :color="roundStat(r).total > 0 ? 'success' : 'grey'"
                  >
                    <v-icon small left>mdi-clipboard-check-outline</v-icon>
                    ผลประเมินรอบ {{ r }}:
                    {{ fmt(roundStat(r).total) }} คน
                    (อนุมัติ {{ fmt(roundStat(r).approved) }}) /
                    {{ fmt(roundStat(r).colleges) }} แห่ง
                  </v-chip>
                  <v-chip
                    small
                    outlined
                    :color="overview.attendance.total > 0 ? 'success' : 'warning'"
                  >
                    <v-icon small left>mdi-calendar-account</v-icon>
                    ข้อมูลการลา {{ fmt(overview.attendance.total) }} คน /
                    {{ fmt(overview.attendance.colleges) }} แห่ง
                  </v-chip>
                </div>
                <div
                  v-if="overview && overview.attendance.total < overview.population.personnel / 2"
                  class="caption warning--text mt-2"
                >
                  <v-icon x-small color="warning">mdi-alert</v-icon>
                  ข้อมูลการลา/สาย/ขาดยังบันทึกไม่ครบ — ผู้ไม่มีข้อมูลจะถือว่าไม่มีวันลา
                  (ตรวจสิทธิ์ข้อ 1(7)-(9) ได้เฉพาะผู้ที่มีข้อมูล)
                </div>
              </v-col>
            </v-row>

            <!-- รายการรอบ -->
            <v-data-table
              :headers="runHeaders"
              :items="runs"
              dense
              hide-default-footer
              disable-pagination
              class="mt-3 elevation-0 run-table"
              no-data-text="ยังไม่มีรอบการประมวลผลในปีนี้ — สร้างรอบใหม่ด้านล่าง"
              :item-class="r => (currentRun && r.run_id === currentRun.run_id ? 'blue lighten-5' : '')"
            >
              <template v-slot:item.run_id="{ item }">#{{ item.run_id }}</template>
              <template v-slot:item.round_mode="{ item }">
                {{ roundModeText(item.round_mode) }}
                <div class="caption grey--text">{{ methodText(item.calc_method) }}</div>
              </template>
              <template v-slot:item.salary_source="{ item }">
                {{ salarySourceText(item.salary_source) }}
              </template>
              <template v-slot:item.run_status="{ item }">
                <v-chip x-small dark :color="item.run_status === 'final' ? 'success' : 'grey'">
                  {{ item.run_status === "final" ? "ยืนยันผลแล้ว" : "ฉบับร่าง" }}
                </v-chip>
              </template>
              <template v-slot:item.processed_colleges="{ item }">
                {{ fmt(item.processed_colleges) }} /
                {{ overview ? fmt(overview.population.colleges) : "-" }}
              </template>
              <template v-slot:item.created_at="{ item }">
                <div class="caption">{{ item.created_at }}</div>
                <div class="caption grey--text">{{ item.created_by_name }}</div>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn x-small color="primary" class="mr-1" @click="selectRun(item)">
                  <v-icon x-small left>mdi-folder-open</v-icon>เปิด
                </v-btn>
                <v-btn
                  v-if="item.run_status === 'draft'"
                  x-small
                  icon
                  color="error"
                  @click="deleteRun(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <v-divider class="my-3" />
            <v-row dense align="center">
              <v-col cols="12" md="3">
                <v-select
                  v-model="newRun.round_mode"
                  :items="roundModeItems"
                  label="รอบผลประเมินที่ใช้"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="newRun.salary_source"
                  :items="salarySourceItems"
                  label="ฐานค่าตอบแทน"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="newRun.calc_method"
                  :items="methodItems"
                  label="วิธีคำนวณ"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="6" md="1">
                <v-text-field v-model.number="newRun.min_score" type="number" label="คะแนนขั้นต่ำ" outlined dense hide-details />
              </v-col>
              <v-col cols="6" md="1">
                <v-text-field v-model.number="newRun.min_months" type="number" label="เดือนขั้นต่ำ" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="newRun.run_note"
                  label="หมายเหตุ (ถ้ามี)"
                  outlined
                  dense
                  hide-details
                  maxlength="255"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-btn block color="info darken-3" dark :loading="busy" @click="createRun">
                  <v-icon left>mdi-plus</v-icon>สร้างรอบใหม่
                </v-btn>
              </v-col>
            </v-row>
            <div class="d-flex align-center mt-3">
              <v-btn color="teal darken-1" dark small @click="openSimulator(null)">
                <v-icon small left>mdi-calculator-variant-outline</v-icon>จำลองการคำนวณรายแห่ง
              </v-btn>
              <v-btn color="brown" outlined small class="ml-2" @click="openHolidays">
                <v-icon small left>mdi-calendar-remove</v-icon>วันหยุดราชการ
              </v-btn>
              <v-btn color="purple" outlined small class="ml-2" @click="openTransfers">
                <v-icon small left>mdi-swap-horizontal</v-icon>ผู้ย้ายมีผล 1 ต.ค.
              </v-btn>
              <span class="caption grey--text ml-3">
                ตรวจการบริหารวงเงิน 4% รายสถานศึกษา พร้อมเหตุผลประกอบรายบุคคล ก่อนประมวลผลจริง (ไม่บันทึกผล)
              </span>
            </div>
            <div class="caption grey--text mt-2">
              ฐานวงเงิน = ค่าตอบแทนทุกคน ณ 1 ก.ย. (ประกาศ คพร.): รวมผู้เกษียณ/ลาออกเดือน ก.ย. ในวงเงิน (ไม่ได้รับการเลื่อน) และผู้ย้ายมีผล 1 ต.ค. คำนวณที่สังกัดเดิม ·
              สถานศึกษาใช้ฐานจากทะเบียนประวัติ (personnel_temporary)
              · ฐานคะแนน = ค่าเฉลี่ยผลประเมินรอบ 1 และรอบ 2 ของปีงบประมาณ (ระเบียบ คพร.) กรณีมีผลรอบเดียว
              ต้องปฏิบัติงานในปีงบประมาณไม่น้อยกว่า 8 เดือน (1 ก.พ. ตรงวันหยุดราชการ ผู้เริ่มงานวันทำการแรกของ ก.พ. นับ 8 เดือน) · คะแนนเฉลี่ยต้องไม่ต่ำกว่า 75 (ระดับดี) · ส่วนกลางกำหนดร้อยละรายคนได้ (ต้องระบุเหตุผล)
            </div>
          </v-card-text>
        </v-card>

        <template v-if="currentRun">
          <!-- ===== STEP 2: ประมวลผลทีละสถานศึกษา ===== -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-2">
              <v-avatar color="info darken-3" size="26" class="mr-2">
                <span class="white--text caption font-weight-bold">2</span>
              </v-avatar>
              ประมวลผลรอบ #{{ currentRun.run_id }}
              <span class="caption ml-2 grey--text">
                ({{ roundModeText(currentRun.round_mode) }} ·
                {{ salarySourceText(currentRun.salary_source) }} ·
                วงเงิน {{ currentRun.budget_pct }}% · สูงสุด {{ currentRun.max_pct }}%)
              </span>
              <v-spacer />
              <v-chip v-if="isFinal" small dark color="success">
                <v-icon small left>mdi-lock</v-icon>ยืนยันผลแล้ว
                {{ currentRun.finalized_at }}
              </v-chip>
            </v-card-title>
            <v-card-text class="pt-4">
              <v-row dense align="center">
                <v-col cols="12" md="7">
                  <v-progress-linear
                    :value="progressPct"
                    height="24"
                    rounded
                    :color="errorCount ? 'warning' : 'success'"
                  >
                    <span class="caption white--text font-weight-bold">
                      ประมวลผลแล้ว {{ fmt(doneCount) }} / {{ fmt(colleges.length) }} แห่ง
                      ({{ progressPct.toFixed(1) }}%)
                      <span v-if="errorCount"> · ผิดพลาด {{ errorCount }}</span>
                    </span>
                  </v-progress-linear>
                </v-col>
                <v-col cols="12" md="5" class="text-right">
                  <v-btn
                    v-if="!processing"
                    color="primary"
                    small
                    class="mr-1"
                    :disabled="isFinal || !colleges.length"
                    @click="processColleges(colleges)"
                  >
                    <v-icon small left>mdi-play</v-icon>ประมวลผลทุกสถานศึกษา
                  </v-btn>
                  <v-btn
                    v-if="!processing"
                    color="warning darken-1"
                    small
                    class="mr-1"
                    :disabled="isFinal || pendingOrErrorCount === 0"
                    @click="processColleges(colleges.filter(c => c.state !== 'ok'))"
                  >
                    <v-icon small left>mdi-replay</v-icon>เฉพาะที่ค้าง/ผิดพลาด
                    ({{ pendingOrErrorCount }})
                  </v-btn>
                  <v-btn v-if="processing" color="error" small @click="stopRequested = true">
                    <v-icon small left>mdi-stop</v-icon>หยุด
                  </v-btn>
                </v-col>
              </v-row>

              <v-row dense class="mt-2">
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="collegeSearch"
                    dense
                    outlined
                    hide-details
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    label="ค้นหาสถานศึกษา / จังหวัด / รหัส"
                  />
                </v-col>
                <v-col cols="12" md="8" class="d-flex align-center">
                  <v-btn-toggle v-model="collegeFilter" dense mandatory>
                    <v-btn x-small value="all">ทั้งหมด</v-btn>
                    <v-btn x-small value="pending">ยังไม่ประมวลผล</v-btn>
                    <v-btn x-small value="error">ผิดพลาด</v-btn>
                    <v-btn x-small value="incomplete">ข้อมูลไม่ครบ</v-btn>
                    <v-btn v-if="isFinal" x-small value="cert_none">ยังไม่ส่งแบบรับรอง ({{ certCount.none }})</v-btn>
                    <v-btn v-if="isFinal" x-small value="cert_submitted">รอส่วนกลางตรวจ ({{ certCount.submitted }})</v-btn>
                    <v-btn v-if="isFinal" x-small value="cert_rejected">ส่งกลับ ({{ certCount.rejected }})</v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <v-data-table
                :headers="collegeHeaders"
                :items="filteredColleges"
                :search="collegeSearch"
                dense
                class="mt-2 elevation-0"
                :footer-props="{ itemsPerPageOptions: [20, 50, 100, -1] }"
                :items-per-page="20"
                item-key="college_code"
              >
                <template v-slot:item.college_name="{ item }">
                  <div class="font-weight-medium">
                    {{ item.college_name }}
                    <v-chip v-if="item.unit_type === 'C'" x-small outlined color="purple">สถาบัน</v-chip>
                    <v-chip v-if="item.unit_type === 'V'" x-small outlined color="teal">ส่วนกลาง</v-chip>
                  </div>
                  <div class="caption grey--text">
                    {{ item.college_code }} · {{ item.province_name || "-" }}
                  </div>
                </template>
                <template v-slot:item.state="{ item }">
                  <v-chip x-small dark :color="stateColor(item.state)">
                    <v-progress-circular
                      v-if="item.state === 'processing'"
                      indeterminate
                      size="10"
                      width="2"
                      class="mr-1"
                    />
                    {{ stateText(item.state) }}
                  </v-chip>
                  <div
                    v-if="item.process_message"
                    class="caption"
                    :class="item.state === 'error' ? 'error--text' : 'grey--text'"
                    style="max-width:220px;white-space:normal"
                  >
                    {{ item.process_message }}
                  </div>
                </template>
                <template v-slot:item.eligible_count="{ item }">
                  <span v-if="item.state === 'ok'">
                    {{ fmt(item.eligible_count) }} / {{ fmt(item.personnel_count) }}
                  </span>
                  <span v-else class="grey--text">- / {{ fmt(item.personnel) }}</span>
                </template>
                <template v-slot:item.budget_year="{ item }">
                  {{ item.state === "ok" ? fmt(item.budget_year) : "-" }}
                </template>
                <template v-slot:item.distributed_year="{ item }">
                  <span v-if="item.state === 'ok'" class="font-weight-bold primary--text">
                    {{ fmt(item.distributed_year) }}
                  </span>
                  <span v-else>-</span>
                </template>
                <template v-slot:item.cert_status="{ item }">
                  <span v-if="!isFinal" class="caption grey--text">หลังยืนยันผล</span>
                  <v-chip
                    v-else
                    x-small
                    dark
                    :color="certColor(item.cert_status)"
                    :disabled="!item.cert_status"
                    @click="openReview(item)"
                  >
                    <v-icon v-if="item.cert_status" x-small left>mdi-file-eye</v-icon>
                    {{ certText(item.cert_status) }}
                  </v-chip>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon
                    x-small
                    color="info"
                    :disabled="item.state !== 'ok'"
                    @click="openCollegeDetail(item)"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    color="primary"
                    :disabled="isFinal || processing"
                    @click="processColleges([item])"
                  >
                    <v-icon small>mdi-refresh</v-icon>
                  </v-btn>
                  <v-btn icon x-small color="teal" title="จำลองการคำนวณ" @click="openSimulator(item.college_code)">
                    <v-icon small>mdi-calculator-variant-outline</v-icon>
                  </v-btn>
                  <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon x-small color="red darken-2" title="เอกสาร PDF" :disabled="item.state !== 'ok'" v-bind="attrs" v-on="on">
                        <v-icon small>mdi-file-pdf-box</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="openPdf('cert', item.college_code)">
                        <v-list-item-icon class="mr-2"><v-icon small>mdi-certificate-outline</v-icon></v-list-item-icon>
                        <v-list-item-title>หนังสือรับรองความถูกต้อง (ผอ.ลงนาม)</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="openPdf('college_report', item.college_code)">
                        <v-list-item-icon class="mr-2"><v-icon small>mdi-account-check-outline</v-icon></v-list-item-icon>
                        <v-list-item-title>รายงานผลเพื่อรับทราบ (ลงชื่อรายคน)</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <!-- ===== STEP 3: รายงานสรุป ===== -->
          <v-card outlined class="mb-4" v-if="summary">
            <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-2">
              <v-avatar color="info darken-3" size="26" class="mr-2">
                <span class="white--text caption font-weight-bold">3</span>
              </v-avatar>
              รายงานสรุปการเลื่อนค่าตอบแทน (เสนอสำนักงบประมาณ)
              <v-spacer />
              <v-btn small outlined color="success" class="mr-1" :loading="exporting" @click="exportExcel">
                <v-icon small left>mdi-microsoft-excel</v-icon>ส่งออก Excel
              </v-btn>
              <v-btn small outlined color="primary" class="mr-1" @click="printReport">
                <v-icon small left>mdi-printer</v-icon>พิมพ์รายงาน
              </v-btn>
              <v-btn small outlined color="red darken-2" class="mr-1" :loading="pdfLoading === 'national_report'" @click="openPdf('national_report')">
                <v-icon small left>mdi-file-pdf-box</v-icon>รายงาน PDF
              </v-btn>
              <v-btn small outlined color="deep-purple" class="mr-1" :loading="auditing" @click="runAudit">
                <v-icon small left>mdi-shield-check</v-icon>ตรวจสอบความถูกต้อง
              </v-btn>
              <v-btn small outlined color="indigo" class="mr-1" @click="openTrace">
                <v-icon small left>mdi-account-search</v-icon>ค้นหาที่มาค่าตอบแทน
              </v-btn>
              <v-btn
                v-if="!isFinal"
                small
                color="success"
                :disabled="processing || totals.processed < summary.expected_colleges"
                @click="finalizeRun"
              >
                <v-icon small left>mdi-lock-check</v-icon>ยืนยันผล
              </v-btn>
              <template v-else>
                <v-chip v-if="currentRun.applied_at" small color="success" dark class="mr-1">
                  <v-icon small left>mdi-database-check</v-icon>
                  บันทึกลงทะเบียนประวัติแล้ว {{ fmt(currentRun.applied_count) }} คน
                </v-chip>
                <v-btn v-if="!currentRun.applied_at" small color="primary" class="mr-1" @click="openApply">
                  <v-icon small left>mdi-database-arrow-right</v-icon>บันทึกลงทะเบียนประวัติ
                </v-btn>
                <v-btn v-else small outlined color="error" class="mr-1" :loading="applying" @click="revertApply">
                  <v-icon small left>mdi-undo</v-icon>ยกเลิกการบันทึก
                </v-btn>
                <v-btn small outlined color="warning darken-2" :disabled="!!currentRun.applied_at" @click="reopenRun">
                  <v-icon small left>mdi-lock-open-variant</v-icon>ยกเลิกการยืนยัน
                </v-btn>
              </template>
            </v-card-title>
            <v-card-text class="pt-4">
              <v-alert
                v-if="totals.processed < summary.expected_colleges"
                type="warning"
                dense
                outlined
                class="mb-3"
              >
                ประมวลผลแล้ว {{ fmt(totals.processed) }} จาก
                {{ fmt(summary.expected_colleges) }} แห่ง —
                ยอดรวมด้านล่างยังไม่ครบทุกสถานศึกษา
              </v-alert>

              <v-row dense>
                <v-col v-for="k in kpis" :key="k.label" cols="6" md="3">
                  <div class="kpi" :style="{ borderLeftColor: k.color }">
                    <div class="kpi-label">{{ k.label }}</div>
                    <div class="kpi-value" :style="{ color: k.color }">{{ k.value }}</div>
                    <div class="kpi-unit">{{ k.unit }}</div>
                  </div>
                </v-col>
              </v-row>

              <div v-if="isFinal" class="d-flex flex-wrap align-center mt-3" style="gap:6px">
                <span class="subtitle-2 mr-1">
                  <v-icon small>mdi-file-sign</v-icon> แบบรับรอง ผอ. ลงนาม:
                </span>
                <v-chip small color="success" dark>รับรองแล้ว {{ fmt(certCount.accepted) }}</v-chip>
                <v-chip small color="info" dark>รอตรวจ {{ fmt(certCount.submitted) }}</v-chip>
                <v-chip small color="error" dark>ส่งกลับ {{ fmt(certCount.rejected) }}</v-chip>
                <v-chip small>ยังไม่ส่ง {{ fmt(certCount.none) }}</v-chip>
                <span class="caption grey--text">จาก {{ fmt(colleges.length) }} แห่ง</span>
              </div>

              <v-progress-linear
                class="mt-3"
                :value="Math.min(budgetUsePct, 100)"
                height="20"
                rounded
                :color="budgetUsePct > 100 ? 'error' : 'success'"
              >
                <span class="caption white--text font-weight-bold">
                  ใช้วงเงิน {{ budgetUsePct.toFixed(2) }}%
                  ({{ fmt(totals.distributed_year) }} / {{ fmt(totals.budget_year) }} บาท/ปี)
                </span>
              </v-progress-linear>

              <!-- ความครบถ้วนของข้อมูล -->
              <div class="subtitle-2 font-weight-bold mt-5 mb-2">
                <v-icon small>mdi-database-check</v-icon> ความครบถ้วนของข้อมูล
              </div>
              <v-row dense>
                <v-col v-for="q in quality" :key="q.label" cols="6" md="2">
                  <v-card outlined class="pa-2 text-center" :class="q.value > 0 ? q.bg : ''">
                    <div class="title" :class="q.value > 0 ? q.color : 'grey--text'">
                      {{ fmt(q.value) }}
                    </div>
                    <div class="caption">{{ q.label }}</div>
                    <div class="caption grey--text">{{ q.hint }}</div>
                  </v-card>
                </v-col>
              </v-row>

              <v-row class="mt-3">
                <v-col cols="12" md="7">
                  <div class="subtitle-2 font-weight-bold mb-1">สรุปรายภาค</div>
                  <v-simple-table dense class="report-table">
                    <thead>
                      <tr>
                        <th>ภาค</th>
                        <th class="text-right">สถานศึกษา</th>
                        <th class="text-right">พนักงาน</th>
                        <th class="text-right">มีสิทธิ์</th>
                        <th class="text-right">ฐาน (บาท/ด.)</th>
                        <th class="text-right">วงเงิน {{ currentRun.budget_pct }}% (บาท/ปี)</th>
                        <th class="text-right">เลื่อนจริง (บาท/ปี)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="r in byRegion" :key="r.region">
                        <td>{{ r.region }}</td>
                        <td class="text-right">{{ fmt(r.colleges) }}</td>
                        <td class="text-right">{{ fmt(r.personnel_count) }}</td>
                        <td class="text-right">{{ fmt(r.eligible_count) }}</td>
                        <td class="text-right">{{ fmt(r.total_salary) }}</td>
                        <td class="text-right">{{ fmt(r.budget_year) }}</td>
                        <td class="text-right font-weight-bold">{{ fmt(r.distributed_year) }}</td>
                      </tr>
                      <tr class="grey lighten-3 font-weight-bold">
                        <td>รวม</td>
                        <td class="text-right">{{ fmt(totals.processed) }}</td>
                        <td class="text-right">{{ fmt(totals.personnel_count) }}</td>
                        <td class="text-right">{{ fmt(totals.eligible_count) }}</td>
                        <td class="text-right">{{ fmt(totals.total_salary) }}</td>
                        <td class="text-right">{{ fmt(totals.budget_year) }}</td>
                        <td class="text-right">{{ fmt(totals.distributed_year) }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
                <v-col cols="12" md="5">
                  <div class="subtitle-2 font-weight-bold mb-1">แยกตามกลุ่มงาน</div>
                  <v-simple-table dense class="report-table">
                    <thead>
                      <tr>
                        <th>กลุ่มงาน</th>
                        <th class="text-right">คน</th>
                        <th class="text-right">มีสิทธิ์</th>
                        <th class="text-right">เลื่อน (บาท/ด.)</th>
                        <th class="text-right">ถึงขั้นสูง</th>
                        <th class="text-right">ตัดที่ 6%</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="g in summary.by_group" :key="g.name_type_group">
                        <td>{{ g.name_type_group || "-" }}</td>
                        <td class="text-right">{{ fmt(g.personnel) }}</td>
                        <td class="text-right">{{ fmt(g.eligible) }}</td>
                        <td class="text-right">{{ fmt(g.distributed_month) }}</td>
                        <td class="text-right">{{ fmt(g.capped) }}</td>
                        <td class="text-right">{{ fmt(g.pct_capped) }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <div class="subtitle-2 font-weight-bold mt-4 mb-1">สาเหตุที่ไม่มีสิทธิ์</div>
                  <v-simple-table dense class="report-table">
                    <tbody>
                      <tr v-for="r in summary.ineligible_reasons" :key="r.reason">
                        <td>{{ r.reason }}</td>
                        <td class="text-right">{{ fmt(r.n) }} คน</td>
                      </tr>
                      <tr v-if="!summary.ineligible_reasons.length">
                        <td class="grey--text">ไม่มี</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </template>
      </base-material-card>
    </v-container>

    <!-- ===== รายละเอียดรายบุคคลของสถานศึกษา ===== -->
    <v-dialog v-model="detailDialog" max-width="1300" scrollable>
      <v-card v-if="detailCollege">
        <v-card-title class="info darken-3 white--text py-2">
          <v-icon left dark>mdi-school</v-icon>
          {{ detailCollege.college_name }}
          <span class="caption ml-2">({{ detailCollege.college_code }})</span>
          <v-spacer />
          <v-btn icon dark @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert v-if="detailCollege.pool_month" dense outlined type="info" class="caption">
            วงเงิน {{ fmt2(detailCollege.pool_month) }} บาท/เดือน · ใช้ {{ fmt(detailCollege.distributed_month) }} ·
            คงเหลือ {{ fmt2(detailCollege.leftover_month) }}
            <span v-if="detailCollege.factor_k"> · อัตรากลาง {{ Number(detailCollege.factor_k).toFixed(4) }}% (คะแนน 100)</span>
            <span v-if="+detailCollege.retired_count || +detailCollege.resigned_count">
              · นับเป็นวงเงินแต่ไม่ได้เลื่อน: เกษียณ {{ detailCollege.retired_count }} ลาออก {{ detailCollege.resigned_count }} คน</span>
            <div v-if="detailCollege.leftover_reason"><strong>เหตุผลที่มีเงินเหลือ:</strong> {{ detailCollege.leftover_reason }}</div>
            <div v-for="(n, i) in parseNotes(detailCollege.calc_notes)" :key="i">• {{ n }}</div>
          </v-alert>
          <v-data-table
            :headers="detailHeaders"
            :items="detailRows"
            :loading="detailLoading"
            dense
            disable-pagination
            hide-default-footer
            fixed-header
            height="520px"
          >
            <template v-slot:item.full_name="{ item }">
              <div class="font-weight-medium">{{ item.full_name }}</div>
              <div class="caption grey--text">{{ item.name_postion }} · {{ item.name_type_group }}</div>
            </template>
            <template v-slot:item.performance_score="{ item }">
              <span v-if="+item.performance_score > 0">
                <strong>{{ +item.performance_score }}</strong>
                <div class="caption grey--text" style="white-space:nowrap">
                  {{ scoreSourceText(item) }}{{ item.assessment_status !== "approved" ? " · " + (item.assessment_status || "-") : "" }}
                </div>
              </span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:item.eligible="{ item }">
              <v-chip x-small dark :color="+item.eligible ? 'success' : 'error'">
                {{ +item.eligible ? "มีสิทธิ์" : "ไม่มีสิทธิ์" }}
              </v-chip>
              <div
                v-if="item.ineligible_reason"
                class="caption error--text"
                style="max-width:220px;white-space:normal"
              >
                {{ item.ineligible_reason }}
              </div>
            </template>
            <template v-slot:item.leave="{ item }">
              <span v-if="+item.has_attendance" class="caption">
                ลา {{ item.total_counted_leave }} · สาย {{ item.late_count }} · ขาด {{ item.absent_count }}
              </span>
              <span v-else class="caption grey--text">ไม่มีข้อมูล</span>
            </template>
            <template v-slot:item.salary_s="{ item }">{{ fmt(item.salary_s) }}</template>
            <template v-slot:item.actual_pct_raw="{ item }">
              {{ +item.eligible ? Number(item.actual_pct_raw).toFixed(3) + "%" : "-" }}
            </template>
            <template v-slot:item.actual_increase_raw="{ item }">
              <span class="info--text text--darken-2">
                {{ +item.eligible ? fmt2(item.actual_increase_raw) : "-" }}
              </span>
            </template>
            <template v-slot:item.explain_text="{ item }">
              <div class="caption" :class="+item.eligible ? '' : 'error--text'" style="white-space:normal;max-width:420px">
                {{ item.explain_text || item.ineligible_reason || "-" }}
              </div>
              <div v-if="item.transfer_note" class="caption purple--text">{{ item.transfer_note }}</div>
              <div v-for="x in refRounds(item)" :key="x.round" class="caption grey--text">
                ที่มาผลประเมินรอบ {{ x.round }} #{{ x.id }} ({{ x.college }}) · ผู้อนุมัติ {{ x.approver || "-" }}
              </div>
            </template>
            <template v-slot:item.actual_pct="{ item }">
              <span style="white-space:nowrap">
                {{ +item.eligible ? Number(item.actual_pct).toFixed(3) + "%" : "-" }}
                <v-btn
                  v-if="canOverride && +item.eligible"
                  icon
                  x-small
                  :color="item.override_pct !== null ? 'deep-orange' : 'grey'"
                  title="กำหนดร้อยละรายคน"
                  @click="openOverride(item)"
                >
                  <v-icon x-small>mdi-pencil</v-icon>
                </v-btn>
              </span>
              <div v-if="item.override_pct !== null" class="caption deep-orange--text" style="white-space:nowrap">
                ส่วนกลางกำหนด {{ +item.override_pct }}%
              </div>
            </template>
            <template v-slot:item.final_rounded="{ item }">
              <span class="font-weight-bold primary--text">
                {{ +item.eligible ? fmt(item.final_rounded) : "-" }}
              </span>
              <div v-if="+item.capped" class="caption warning--text">ถึงขั้นสูง</div>
              <div v-if="+item.pct_capped" class="caption warning--text">ตัดที่ 6%</div>
            </template>
            <template v-slot:item.new_salary="{ item }">
              <span class="font-weight-bold">{{ fmt(item.new_salary) }}</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ===== จำลองการคำนวณรายแห่ง ===== -->
    <v-dialog v-model="simDialog" fullscreen scrollable>
      <v-card>
        <v-card-title class="teal darken-1 white--text py-2">
          <v-icon left dark>mdi-calculator-variant-outline</v-icon>
          จำลองการคำนวณรายแห่ง ปีงบประมาณ {{ compYear }}
          <v-spacer />
          <v-btn icon dark @click="simDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <CompensationSimulator
            v-if="simDialog"
            :key="(simCollege || '') + compYear"
            :comp-year="Number(compYear)"
            :default-round-mode="currentRun ? currentRun.round_mode : newRun.round_mode"
            :default-salary-source="currentRun ? currentRun.salary_source : newRun.salary_source"
            :initial-college="simCollege"
            @error="msg => notify(msg, 'error')"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ===== บันทึกค่าตอบแทนใหม่ลงทะเบียนประวัติ ===== -->
    <v-dialog v-model="applyDialog" max-width="720" persistent>
      <v-card>
        <v-card-title class="primary white--text py-2">
          <v-icon left dark>mdi-database-arrow-right</v-icon>บันทึกค่าตอบแทนใหม่ลงทะเบียนประวัติ
        </v-card-title>
        <v-card-text class="pt-4">
          <v-progress-linear v-if="!applyPreview" indeterminate />
          <template v-if="applyPreview">
            <p>
              ระบบจะปรับ <strong>ค่าตอบแทนปัจจุบัน</strong> ในทะเบียนประวัติเป็นค่าตอบแทนใหม่ และเพิ่ม
              <strong>ประวัติค่าตอบแทน</strong> วันที่มีผล {{ applyPreview.effective_date }}
              (เลขที่อ้างอิง "{{ applyPreview.marker }}") — ยกเลิกการบันทึกภายหลังได้
            </p>
            <v-simple-table dense class="mb-3">
              <tbody>
                <tr><td>บันทึกได้</td><td class="text-right font-weight-bold">{{ fmt(applyPreview.to_apply) }} คน</td></tr>
                <tr><td>บันทึกไปแล้ว (ข้าม)</td><td class="text-right">{{ fmt(applyPreview.already) }} คน</td></tr>
                <tr><td>ขัดแย้ง (ไม่บันทึก ต้องตรวจสอบ)</td>
                  <td class="text-right" :class="applyPreview.conflicts.length ? 'error--text font-weight-bold' : ''">
                    {{ fmt(applyPreview.conflicts.length) }} คน</td></tr>
                <tr><td>แบบรับรอง ผอ. ที่ส่วนกลางรับรองแล้ว</td>
                  <td class="text-right">{{ fmt(applyPreview.cert.accepted || 0) }} / {{ fmt(applyPreview.cert.colleges) }} แห่ง</td></tr>
              </tbody>
            </v-simple-table>
            <v-alert v-if="(applyPreview.cert.accepted || 0) < applyPreview.cert.colleges" type="warning" dense outlined>
              ยังมีสถานศึกษาที่ส่วนกลางยังไม่รับรองแบบรายงาน — ควรบันทึกหลังรับรองครบและได้รับอนุมัติงบประมาณแล้ว
            </v-alert>
            <v-expansion-panels v-if="applyPreview.conflicts.length" flat class="mb-2">
              <v-expansion-panel>
                <v-expansion-panel-header class="py-1 error--text">รายชื่อที่ขัดแย้ง {{ applyPreview.conflicts.length }} คน</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="c in applyPreview.conflicts.slice(0, 100)" :key="c.id_card" class="caption">
                    {{ c.full_name }} ({{ c.college_code }}) — {{ c.reason }}
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <v-checkbox v-model="applyConfirm" dense hide-details
              label="ตรวจสอบแล้ว ยืนยันการปรับค่าตอบแทนในทะเบียนประวัติ" />
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="applyDialog = false">ปิด</v-btn>
          <v-spacer />
          <v-btn color="primary" :loading="applying" :disabled="!applyPreview || !applyConfirm || !applyPreview.to_apply"
            @click="confirmApply">
            บันทึก {{ applyPreview ? fmt(applyPreview.to_apply) : "" }} คน
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== ตรวจแบบรับรองที่สถานศึกษาแนบ ===== -->
    <v-dialog v-model="reviewDialog" max-width="640">
      <v-card v-if="reviewCollege">
        <v-card-title class="info darken-3 white--text py-2">
          <v-icon left dark>mdi-file-sign</v-icon>
          แบบรับรอง — {{ reviewCollege.college_name }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-simple-table dense>
            <tbody>
              <tr><td width="160">สถานะ</td><td>
                <v-chip x-small dark :color="certColor(reviewCollege.cert_status)">
                  {{ certText(reviewCollege.cert_status) }}
                </v-chip>
              </td></tr>
              <tr><td>ผู้อำนวยการผู้ลงนาม</td><td>{{ reviewCollege.cert_director_name }}</td></tr>
              <tr><td>ไฟล์</td><td>
                {{ reviewCollege.cert_file_name }}
                <v-btn x-small text color="primary" :loading="downloading" @click="openCertFile(reviewCollege)">
                  <v-icon x-small left>mdi-eye</v-icon>เปิดดู
                </v-btn>
              </td></tr>
              <tr><td>แนบเมื่อ</td><td>{{ reviewCollege.cert_uploaded_at }}</td></tr>
              <tr><td>รหัสตรวจสอบเอกสาร</td><td>
                <code class="font-weight-bold">{{ reviewDocCode || "..." }}</code>
                <div class="caption grey--text">ต้องตรงกับรหัสท้ายหนังสือรับรองในไฟล์ที่แนบ — ถ้าไม่ตรง แสดงว่าข้อมูลเปลี่ยนหลังพิมพ์ ให้ส่งกลับพิมพ์ใหม่</div>
              </td></tr>
              <tr v-if="reviewCollege.cert_college_note"><td>หมายเหตุสถานศึกษา</td>
                <td class="warning--text text--darken-2">{{ reviewCollege.cert_college_note }}</td></tr>
              <tr v-if="reviewCollege.cert_reviewed_at"><td>ตรวจล่าสุด</td>
                <td>{{ reviewCollege.cert_reviewed_at }} {{ reviewCollege.cert_reviewed_by_name }}
                  <div class="caption">{{ reviewCollege.cert_review_note }}</div></td></tr>
            </tbody>
          </v-simple-table>
          <v-textarea
            v-model="reviewNote"
            class="mt-3"
            outlined
            dense
            rows="2"
            counter="500"
            label="หมายเหตุ / เหตุผลที่ส่งกลับ (จำเป็นเมื่อส่งกลับ)"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="reviewDialog = false">ปิด</v-btn>
          <v-spacer />
          <v-btn color="error" outlined :loading="reviewing" @click="submitReview('rejected')">
            <v-icon left small>mdi-undo</v-icon>ส่งกลับแก้ไข
          </v-btn>
          <v-btn color="success" :loading="reviewing" @click="submitReview('accepted')">
            <v-icon left small>mdi-check-decagram</v-icon>รับรอง
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== ส่วนกลางกำหนดร้อยละรายคน ===== -->
    <v-dialog v-model="overrideDialog" max-width="640" persistent>
      <v-card v-if="overrideRow">
        <v-toolbar dense dark flat color="deep-orange" class="flex-grow-0">
          <v-icon left>mdi-percent</v-icon>
          <v-toolbar-title class="subtitle-1">กำหนดร้อยละรายคน — {{ overrideRow.full_name }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-alert type="info" dense text class="caption">
            ค่าตอบแทน ณ 1 ก.ย. {{ fmt(overrideRow.salary_s) }} บาท · ฐานคะแนน {{ +overrideRow.performance_score }} ·
            ปัจจุบัน {{ Number(overrideRow.actual_pct).toFixed(3) }}% = {{ fmt(overrideRow.final_rounded) }} บาท
            <br />ผู้มีสิทธิ์คนอื่นในหน่วยงานจะใช้อัตรากลางจากวงเงินที่เหลือ (ระบบประมวลผลหน่วยงานนี้ใหม่ทันที)
            · บันทึกประวัติผู้กำหนด/เหตุผลทุกครั้ง
          </v-alert>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="overrideForm.pct"
                type="number"
                step="0.001"
                min="0"
                :max="+currentRun.max_pct"
                label="ร้อยละที่เลื่อน *"
                suffix="%"
                outlined
                dense
                :hint="overrideAmountText"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" md="8">
              <v-textarea v-model="overrideForm.reason" label="เหตุผล (บันทึกไว้ตรวจสอบ) *" rows="2" outlined dense counter="500" />
            </v-col>
          </v-row>
          <div v-if="overrideLog.length" class="mt-2">
            <div class="overline">ประวัติการกำหนด</div>
            <div v-for="l in overrideLog" :key="l.id" class="caption">
              {{ l.acted_at }} · {{ l.acted_by_name }} ·
              {{ l.action === "set" ? `กำหนด ${l.old_pct !== null ? +l.old_pct + "% → " : ""}${+l.new_pct}%` : `ยกเลิก (เดิม ${+l.old_pct}%)` }}
              — {{ l.reason }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text :disabled="overrideSaving" @click="overrideDialog = false">ปิด</v-btn>
          <v-spacer />
          <v-btn v-if="overrideRow.override_pct !== null" outlined color="grey darken-1" :loading="overrideSaving" @click="saveOverride(true)">
            ยกเลิกการกำหนด (กลับใช้อัตรากลาง)
          </v-btn>
          <v-btn color="deep-orange" dark :loading="overrideSaving" @click="saveOverride(false)">บันทึกและประมวลผลใหม่</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== ผู้ย้ายเริ่มปฏิบัติงาน ณ ที่ใหม่ 1 ต.ค. — คำนวณที่สถานศึกษาเดิม ===== -->
    <v-dialog v-model="transferDialog" max-width="1100" scrollable>
      <v-card>
        <v-toolbar dense dark flat color="purple" class="flex-grow-0">
          <v-icon left>mdi-swap-horizontal</v-icon>
          <v-toolbar-title class="subtitle-1">ผู้ย้ายเริ่มปฏิบัติงาน ณ ที่ใหม่ 1 ต.ค. {{ compYear }} — คำนวณที่สถานศึกษาเดิม</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="transferDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-alert type="info" dense text class="caption">
            ผู้ย้ายที่เริ่มปฏิบัติงาน ณ สถานศึกษาใหม่ 1 ต.ค. ให้คำนวณการเลื่อนค่าตอบแทนกับ<strong>สถานศึกษาเดิม</strong>
            (ค่าตอบแทน ผลประเมิน วันลา ณ สถานศึกษาเดิม นับในวงเงิน 4% ของสถานศึกษาเดิม) ผลปรากฏที่สถานศึกษาเดิม
            ผอ.สถานศึกษาเดิมเป็นผู้รับรอง · สถานศึกษาใหม่เห็นรายชื่อเพื่อทราบ
          </v-alert>
          <div class="subtitle-2 mb-1">ระบบตรวจพบแล้ว ({{ transferData.detected.length }} คน)</div>
          <v-simple-table dense class="mb-4">
            <thead><tr><th>ชื่อ-สกุล</th><th>สถานศึกษาเดิม (คำนวณที่นี่)</th><th>สถานศึกษาใหม่</th><th>มีผล</th><th>แหล่งข้อมูล</th><th></th></tr></thead>
            <tbody>
              <tr v-for="t in transferData.detected" :key="'d' + t.id_card">
                <td>{{ t.full_name }}<div class="caption grey--text">{{ t.id_card }}</div></td>
                <td>{{ t.from_name }}</td>
                <td>{{ t.to_name }}</td>
                <td>{{ t.date }}</td>
                <td class="caption">{{ t.source }}</td>
                <td>
                  <v-btn v-if="t.manual" x-small text color="error" :loading="transferBusy === t.id_card" @click="unmarkTransfer(t)">ยกเลิก</v-btn>
                </td>
              </tr>
              <tr v-if="!transferData.detected.length"><td colspan="6" class="grey--text text-center">ไม่มี</td></tr>
            </tbody>
          </v-simple-table>
          <div class="subtitle-2 mb-1">
            คำขอย้ายที่ยืนยันก่อน 1 ต.ค. ({{ transferData.candidates.length }} คน) — ระบบถือว่าย้ายแล้ว (คำนวณที่สถานศึกษาใหม่)
          </div>
          <div class="caption grey--text mb-1">
            คำขอย้ายไม่มีช่องวันที่มีผล ถ้าคนใดเริ่มปฏิบัติงาน ณ ที่ใหม่ 1 ต.ค. กด "มีผล 1 ต.ค." พร้อมเลขที่คำสั่ง/เหตุผล
            ระบบจะย้ายไปคำนวณที่สถานศึกษาเดิม
          </div>
          <v-simple-table dense>
            <thead><tr><th>ชื่อ-สกุล</th><th>จาก</th><th>ไป</th><th>ประเภท</th><th>ยืนยันเมื่อ</th><th width="280">คำสั่ง/เหตุผล</th></tr></thead>
            <tbody>
              <tr v-for="t in transferData.candidates" :key="'c' + t.id_card">
                <td>{{ t.full_name }}<div class="caption grey--text">{{ t.id_card }}</div></td>
                <td>{{ t.from_name }}</td>
                <td>{{ t.to_name }}</td>
                <td class="caption">{{ { export: "ย้ายออก", pull: "ดึงเข้า", swap: "สับเปลี่ยน" }[t.direction] || t.direction }}</td>
                <td class="caption">{{ t.confirmed_at }}</td>
                <td>
                  <div class="d-flex align-center">
                    <v-text-field v-model="transferReason[t.id_card]" dense hide-details placeholder="เลขที่คำสั่ง/เหตุผล" class="mr-1" />
                    <v-btn x-small color="purple" dark :loading="transferBusy === t.id_card" @click="markTransfer(t)">มีผล 1 ต.ค.</v-btn>
                  </div>
                </td>
              </tr>
              <tr v-if="!transferData.candidates.length"><td colspan="6" class="grey--text text-center">ไม่มี</td></tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ===== วันหยุดราชการ ===== -->
    <v-dialog v-model="holidayDialog" max-width="620" scrollable>
      <v-card>
        <v-toolbar dense dark flat color="brown" class="flex-grow-0">
          <v-icon left>mdi-calendar-remove</v-icon>
          <v-toolbar-title class="subtitle-1">วันหยุดราชการ ปีงบประมาณ {{ compYear }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="holidayDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-alert type="info" dense text class="caption">
            ใช้ตัดสินข้อยกเว้นตามแนวปฏิบัติสำนักงาน ก.พ.: ถ้า 1 ก.พ. ตรงกับวันหยุดราชการ ผู้เริ่มงานวันทำการแรกของเดือน ก.พ.
            (เช่น 2 ก.พ.) นับอายุงานเป็น 8 เดือน มีสิทธิ์เลื่อนค่าตอบแทน · เสาร์-อาทิตย์ระบบคำนวณเอง
            บันทึกเฉพาะวันหยุดนักขัตฤกษ์/วันหยุดพิเศษ
            <div class="mt-1">
              <strong>ก.พ. {{ compYear }}:</strong> 1 ก.พ. ตรงกับ{{ febInfo.text }} · วันทำการแรก {{ febInfo.firstWork }} ก.พ.
            </div>
          </v-alert>
          <v-row dense>
            <v-col cols="5"><v-text-field v-model="holidayForm.holiday_date" type="date" label="วันที่ (ค.ศ.)" outlined dense hide-details /></v-col>
            <v-col cols="5"><v-text-field v-model="holidayForm.holiday_name" label="ชื่อวันหยุด" outlined dense hide-details /></v-col>
            <v-col cols="2"><v-btn color="brown" dark block @click="saveHoliday">เพิ่ม</v-btn></v-col>
          </v-row>
          <v-simple-table dense class="mt-2">
            <tbody>
              <tr v-for="h in holidays" :key="h.holiday_date">
                <td width="140">{{ h.holiday_date }}</td>
                <td>{{ h.holiday_name }}</td>
                <td width="40"><v-btn icon x-small color="error" @click="deleteHoliday(h)"><v-icon small>mdi-delete</v-icon></v-btn></td>
              </tr>
              <tr v-if="!holidays.length"><td colspan="3" class="grey--text text-center">ยังไม่มีวันหยุดที่บันทึกในปีงบประมาณนี้</td></tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ===== ผลการตรวจสอบความถูกต้อง (คำนวณซ้ำฝั่งเซิร์ฟเวอร์) ===== -->
    <v-dialog v-model="auditDialog" max-width="900" scrollable>
      <v-card v-if="auditResult">
        <v-toolbar dense dark flat color="deep-purple" class="flex-grow-0">
          <v-icon left>mdi-shield-check</v-icon>
          <v-toolbar-title class="subtitle-1">ผลการตรวจสอบความถูกต้อง (รอบ #{{ currentRun && currentRun.run_id }})</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="auditDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-alert :type="auditResult.problems.length ? 'error' : 'success'" dense outlined>
            หน่วยงานถูกต้อง {{ fmt(auditResult.totals.colleges_ok) }} / {{ fmt(auditResult.totals.colleges) }} แห่ง ·
            รายบุคคลถูกต้อง {{ fmt(auditResult.totals.persons_ok) }} / {{ fmt(auditResult.totals.persons) }} คน
            <div class="caption">ตรวจเมื่อ {{ auditResult.audited_at }}</div>
          </v-alert>
          <div class="caption mb-3">
            ระบบคำนวณซ้ำทุกคนจากข้อมูลที่บันทึกไว้ (ไม่พึ่งการคำนวณบนหน้าเว็บ) โดยตรวจ:
            ค่าตอบแทนใหม่ = เดิม + เงินเลื่อน · เงินเลื่อน = ปัดขึ้นหลักสิบ(ค่าตอบแทน × min(เพดาน, k × คะแนน ÷ 100) ÷ 100)
            ด้วยค่า k ของหน่วยงาน · ไม่เกินเพดาน/ขั้นสูง · ผู้ไม่มีสิทธิ์ได้ 0 และมีเหตุผล ·
            ยอดรวมไม่เกินวงเงิน 4% · ยอดรวมและจำนวนคนตรงกับรายบุคคล · มีคำอธิบายเงินเหลือ
          </div>
          <v-expansion-panels v-if="auditResult.problems.length" accordion>
            <v-expansion-panel v-for="p in auditResult.problems" :key="p.college_code">
              <v-expansion-panel-header>
                <span>
                  <strong>{{ p.college_name }}</strong>
                  <span class="caption grey--text">({{ p.college_code }})</span>
                  <v-chip x-small color="error" dark class="ml-2">{{ p.issues.length + p.persons.length }} รายการ</v-chip>
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-for="(i, k) in p.issues" :key="'c' + k" class="error--text">• {{ i }}</div>
                <div v-for="d in p.persons" :key="d.id_card" class="mt-1">
                  <strong>{{ d.full_name }}</strong> <span class="caption">{{ d.id_card }}</span>
                  <div v-for="(i, k) in d.issues" :key="k" class="caption error--text ml-3">• {{ i }}</div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ===== ค้นหาที่มาค่าตอบแทนรายบุคคล ===== -->
    <v-dialog v-model="traceDialog" max-width="1000" scrollable>
      <v-card>
        <v-toolbar dense dark flat color="indigo" class="flex-grow-0">
          <v-icon left>mdi-account-search</v-icon>
          <v-toolbar-title class="subtitle-1">ค้นหาที่มาค่าตอบแทน (รอบ #{{ currentRun && currentRun.run_id }})</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="traceDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="traceQ"
            outlined
            dense
            autofocus
            prepend-inner-icon="mdi-magnify"
            label="ชื่อ-นามสกุล หรือเลขบัตรประชาชน (อย่างน้อย 2 ตัวอักษร)"
            @keyup.enter="runTrace"
          >
            <template v-slot:append-outer>
              <v-btn color="indigo" dark small :loading="tracing" @click="runTrace">ค้นหา</v-btn>
            </template>
          </v-text-field>
          <div v-if="traceResults && !traceResults.length" class="grey--text text-center py-4">ไม่พบข้อมูลในรอบนี้</div>
          <v-card v-for="r in traceResults || []" :key="r.college_code + r.id_card" outlined class="mb-3">
            <v-card-title class="subtitle-1 py-2 grey lighten-4">
              {{ r.full_name }}
              <span class="caption grey--text ml-2">{{ r.id_card }} · {{ r.name_postion }} · {{ r.name_type_group }}</span>
              <v-spacer />
              <v-chip x-small dark :color="r.audit.ok ? 'success' : 'error'">
                <v-icon x-small left>{{ r.audit.ok ? "mdi-check" : "mdi-alert" }}</v-icon>
                {{ r.audit.ok ? "คำนวณซ้ำตรงกัน" : "ตรวจพบความผิดปกติ" }}
              </v-chip>
            </v-card-title>
            <v-card-text class="pt-3">
              <v-alert v-if="!r.audit.ok" type="error" dense outlined class="mb-2">
                <div v-for="(i, k) in r.audit.issues" :key="k">• {{ i }}</div>
              </v-alert>
              <v-row dense>
                <v-col cols="12" md="6">
                  <div class="overline">ขั้นที่ 1 · ข้อมูลตั้งต้น (ค่าตอบแทน ณ 1 ก.ย.)</div>
                  <v-simple-table dense>
                    <tbody>
                      <tr>
                        <td width="42%">หน่วยงานที่นับวงเงิน</td>
                        <td>{{ r.college_name }} <span class="caption">({{ r.college_code }})</span></td>
                      </tr>
                      <tr><td>สถานะ</td><td>{{ baseStatusText(r.base_status) }}</td></tr>
                      <tr v-if="r.transfer_note"><td>การย้าย</td><td class="purple--text">{{ r.transfer_note }}</td></tr>
                      <tr>
                        <td>ค่าตอบแทนฐาน</td>
                        <td>
                          {{ fmt(r.salary_s) }} บาท
                          <div v-if="r.salary_note" class="caption grey--text">{{ r.salary_note }}</div>
                        </td>
                      </tr>
                      <tr>
                        <td>ผลการประเมิน</td>
                        <td>
                          <span v-if="+r.performance_score > 0">
                            ฐานคะแนน <strong>{{ +r.performance_score }}</strong> ({{ r.assessment_status || "-" }})
                            <div class="caption">{{ scoreSourceText(r) }}</div>
                          </span>
                          <span v-else class="grey--text">ไม่มี</span>
                          <div v-for="x in refRounds(r)" :key="x.round" class="caption grey--text">
                            รอบ {{ x.round }}: ผลประเมิน #{{ x.id }} ({{ x.college }}){{ x.score ? " · " + x.score + " คะแนน" : "" }}
                            · ผู้อนุมัติ {{ x.approver || "-" }}
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td>อายุงานในปีงบประมาณ</td>
                        <td>
                          <span v-if="r.service_months !== null && r.service_months !== undefined">{{ r.service_months }} เดือน</span>
                          <span v-else class="grey--text">ไม่มีวันที่บรรจุ</span>
                          <div class="caption grey--text">เกณฑ์ ≥ {{ currentRun.min_months }} เดือน · 1 ก.พ. ตรงวันหยุด เริ่มวันทำการแรกของ ก.พ. นับ 8 เดือน</div>
                        </td>
                      </tr>
                      <tr>
                        <td>การลา/สาย/ขาด</td>
                        <td>
                          <span v-if="+r.has_attendance">
                            ลา {{ r.total_counted_leave }} วัน · ลากิจ {{ r.personal_leave_times }} ครั้ง · สาย {{ r.late_count }} · ขาด {{ r.absent_count }}
                          </span>
                          <span v-else class="grey--text">ไม่มีข้อมูล</span>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="overline">ขั้นที่ 2 · วงเงินของหน่วยงาน</div>
                  <v-simple-table dense>
                    <tbody>
                      <tr>
                        <td width="42%">ค่าตอบแทนรวม × 4%</td>
                        <td>{{ fmt(r.college_total_salary) }} × 4% = <strong>{{ fmt2(r.pool_month) }}</strong> บาท</td>
                      </tr>
                      <tr><td>บุคลากรในวงเงิน</td><td>{{ fmt(r.college_personnel) }} คน (มีสิทธิ์ {{ fmt(r.college_eligible) }} คน)</td></tr>
                      <tr v-if="r.factor_k !== null">
                        <td>อัตรากลาง (k)</td>
                        <td>
                          {{ Number(r.factor_k).toFixed(4) }}
                          <div class="caption grey--text">ร้อยละที่ได้ = min(เพดาน, k × คะแนน ÷ 100)</div>
                        </td>
                      </tr>
                      <tr>
                        <td>เงินเหลือ</td>
                        <td>
                          {{ fmt2(r.leftover_month) }} บาท
                          <div v-if="r.leftover_reason" class="caption grey--text">{{ r.leftover_reason }}</div>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
              </v-row>
              <div class="overline mt-2">ขั้นที่ 3 · เกณฑ์การมีสิทธิ์</div>
              <div v-for="c in parseNotes(r.checks_json)" :key="c.key" class="caption">
                <v-icon x-small :color="c.pass ? 'success' : c.blocking ? 'error' : 'warning'">
                  {{ c.pass ? "mdi-check-circle" : c.blocking ? "mdi-close-circle" : "mdi-alert-circle" }}
                </v-icon>
                {{ c.label }}: <span class="grey--text text--darken-1">{{ c.detail }}</span>
              </div>
              <div class="overline mt-2">ขั้นที่ 4 · ผลการคำนวณ</div>
              <v-alert dense text :color="+r.eligible ? 'success' : 'grey'" class="mb-0">
                <div v-if="+r.eligible">
                  ร้อยละก่อนปัด {{ Number(r.actual_pct_raw).toFixed(3) }}% → เงินเลื่อนก่อนปัด {{ fmt2(r.actual_increase_raw) }} บาท
                  → <strong>ปัดขึ้นหลักสิบ {{ fmt(r.final_rounded) }} บาท</strong>
                  <span v-if="+r.capped" class="warning--text">(ชนขั้นสูงกลุ่มงาน)</span>
                  · ค่าตอบแทนใหม่ <strong>{{ fmt(r.new_salary) }}</strong> บาท
                  <span v-if="r.audit.expected != null" class="caption">· ระบบคำนวณซ้ำได้ {{ fmt(r.audit.expected) }} บาท</span>
                </div>
                <div v-else>ไม่ได้รับการเลื่อน — {{ r.ineligible_reason || "-" }}</div>
                <div class="caption mt-1">{{ r.explain_text }}</div>
              </v-alert>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="5000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
  calculateCollege,
  calculateCollegeV2,
  roundModeLabel,
  scoreSourceText,
  febFirstWorkday
} from "@/utils/govemCompensationEngine";
import CompensationSimulator from "../../../components/govem/CompensationSimulator.vue";

const API = "govem_compensation_central.php";
const CONCURRENCY = 3; // จำนวนสถานศึกษาที่ประมวลผลพร้อมกัน

export default {
  name: "CentralCompensation",
  components: { CompensationSimulator },

  data() {
    const thisYear = new Date().getFullYear() + 543;
    return {
      ApiKey: "HRvec2021",
      years: [thisYear - 2, thisYear - 1, thisYear, thisYear + 1],
      compYear: thisYear,
      overview: null,
      runs: [],
      currentRun: null,
      colleges: [],
      summary: null,
      busy: false,
      processing: false,
      stopRequested: false,
      exporting: false,
      collegeSearch: "",
      collegeFilter: "all",
      newRun: { round_mode: "average", salary_source: "pt", run_note: "", calc_method: "equal_factor", min_score: 75, min_months: 8 },
      methodItems: [
        { text: "ร้อยละร่วมตามคะแนน (ปัดขึ้นเท่ากันทุกคน)", value: "equal_factor" },
        { text: "สูตรเดิมของสถานศึกษา", value: "legacy" }
      ],
      simDialog: false,
      simCollege: null,
      // ระเบียบ คพร.: ฐานคะแนน = ค่าเฉลี่ยผลประเมิน 2 รอบของปีงบประมาณ (มีรอบเดียวต้องปฏิบัติงาน ≥ 8 เดือน)
      roundModeItems: [{ text: "เฉลี่ยผลประเมิน 2 รอบ (ตามระเบียบ คพร.)", value: "average" }],
      salarySourceItems: [
        { text: "ทะเบียนประวัติ (เหมือนสถานศึกษา)", value: "pt" },
        { text: "กรอบอัตรากำลัง (government_employee)", value: "ge" }
      ],
      detailDialog: false,
      detailLoading: false,
      detailCollege: null,
      detailRows: [],
      reviewDialog: false,
      reviewCollege: null,
      reviewDocCode: "",
      overrideDialog: false,
      overrideRow: null,
      overrideForm: { pct: 0, reason: "" },
      overrideLog: [],
      overrideSaving: false,
      transferDialog: false,
      transferData: { detected: [], candidates: [] },
      transferReason: {},
      transferBusy: null,
      holidayDialog: false,
      holidays: [],
      holidayForm: { holiday_date: "", holiday_name: "" },
      auditDialog: false,
      auditResult: null,
      auditing: false,
      traceDialog: false,
      traceQ: "",
      traceResults: null,
      tracing: false,
      pdfLoading: null,
      reviewNote: "",
      reviewing: false,
      applyDialog: false,
      applyPreview: null,
      applyConfirm: false,
      applying: false,
      downloading: false,
      snackbar: { show: false, color: "success", text: "" },
      runHeaders: [
        { text: "รอบ", value: "run_id", width: 60 },
        { text: "ผลประเมิน", value: "round_mode" },
        { text: "ฐานค่าตอบแทน", value: "salary_source" },
        { text: "สถานะ", value: "run_status" },
        { text: "ประมวลผลแล้ว (แห่ง)", value: "processed_colleges" },
        { text: "หมายเหตุ", value: "run_note" },
        { text: "สร้างเมื่อ", value: "created_at" },
        { text: "", value: "actions", sortable: false, align: "right" }
      ],
      collegeHeaders: [
        { text: "สถานศึกษา", value: "college_name" },
        { text: "ภาค", value: "region_name" },
        { text: "สถานะ", value: "state", width: 180 },
        { text: "มีสิทธิ์/ทั้งหมด", value: "eligible_count", align: "center" },
        { text: "วงเงิน 4% (บาท/ปี)", value: "budget_year", align: "right" },
        { text: "เลื่อนจริง (บาท/ปี)", value: "distributed_year", align: "right" },
        { text: "แบบรับรอง ผอ.", value: "cert_status", width: 150 },
        { text: "", value: "actions", sortable: false, width: 80 },
        // ใช้ค้นหาเท่านั้น
        { text: "", value: "province_name", align: " d-none" },
        { text: "", value: "college_code", align: " d-none" }
      ],
      detailHeaders: [
        { text: "ชื่อ-สกุล", value: "full_name" },
        { text: "ค่าตอบแทน", value: "salary_s", align: "right" },
        { text: "ฐานคะแนน (เฉลี่ย 2 รอบ)", value: "performance_score" },
        { text: "การลา", value: "leave", sortable: false },
        { text: "สิทธิ์", value: "eligible" },
        { text: "% เลื่อนจริงก่อนปัดเศษ", value: "actual_pct_raw", align: "center" },
        { text: "เลื่อนจริงก่อนปัดเศษ (บาท)", value: "actual_increase_raw", align: "right" },
        { text: "เลื่อนจริง (บาท/ด.)", value: "final_rounded", align: "right" },
        { text: "% เลื่อนจริง", value: "actual_pct", align: "center" },
        { text: "เหตุผลประกอบการคำนวณ", value: "explain_text", sortable: false },
        { text: "ค่าตอบแทนใหม่", value: "new_salary", align: "right" }
      ]
    };
  },

  computed: {
    isFinal() {
      return this.currentRun && this.currentRun.run_status === "final";
    },
    // กำหนดร้อยละรายคนได้เฉพาะรอบร่าง วิธีร้อยละร่วมตามคะแนน และไม่ได้กำลังประมวลผลทั้งประเทศ
    canOverride() {
      return !!this.currentRun && this.currentRun.run_status === "draft" &&
        this.currentRun.calc_method !== "legacy" && !this.processing;
    },
    overrideAmountText() {
      if (!this.overrideRow) return "";
      const pct = Number(this.overrideForm.pct);
      if (!(pct >= 0)) return "";
      const amt = Math.ceil((+this.overrideRow.salary_s * pct) / 100 / 10 - 1e-9) * 10;
      return `≈ ${this.fmt(amt)} บาท/เดือน (ปัดขึ้นหลักสิบ)`;
    },
    febInfo() {
      const y = this.compYear - 543;
      const dates = this.holidays.map(h => h.holiday_date);
      const wd = new Date(Date.UTC(y, 1, 1)).getUTCDay();
      const named = this.holidays.find(h => h.holiday_date === `${y}-02-01`);
      const text = named ? `วันหยุดราชการ (${named.holiday_name})` : wd === 0 ? "วันอาทิตย์" : wd === 6 ? "วันเสาร์" : "วันทำการ";
      return { text, firstWork: febFirstWorkday(y, dates) };
    },
    doneCount() {
      return this.colleges.filter(c => c.state === "ok").length;
    },
    errorCount() {
      return this.colleges.filter(c => c.state === "error").length;
    },
    pendingOrErrorCount() {
      return this.colleges.filter(c => c.state !== "ok").length;
    },
    progressPct() {
      return this.colleges.length ? (this.doneCount / this.colleges.length) * 100 : 0;
    },
    filteredColleges() {
      switch (this.collegeFilter) {
        case "pending":
          return this.colleges.filter(c => c.state === "pending");
        case "error":
          return this.colleges.filter(c => c.state === "error");
        case "incomplete":
          return this.colleges.filter(
            c => c.state === "ok" && (+c.no_score_count > 0 || +c.data_issue_count > 0)
          );
        case "cert_none":
          return this.colleges.filter(c => !c.cert_status);
        case "cert_submitted":
          return this.colleges.filter(c => c.cert_status === "submitted");
        case "cert_rejected":
          return this.colleges.filter(c => c.cert_status === "rejected");
        default:
          return this.colleges;
      }
    },
    certCount() {
      const n = s => this.colleges.filter(c => (c.cert_status || null) === s).length;
      return { none: n(null), submitted: n("submitted"), accepted: n("accepted"), rejected: n("rejected") };
    },
    totals() {
      const rows = (this.summary && this.summary.colleges) || [];
      const sum = k => rows.reduce((s, r) => s + (+r[k] || 0), 0);
      return {
        processed: rows.length,
        personnel_count: sum("personnel_count"),
        eligible_count: sum("eligible_count"),
        no_score_count: sum("no_score_count"),
        unapproved_score_count: sum("unapproved_score_count"),
        no_attendance_count: sum("no_attendance_count"),
        data_issue_count: sum("data_issue_count"),
        capped_count: sum("capped_count"),
        pct_capped_count: sum("pct_capped_count"),
        total_salary: sum("total_salary"),
        budget_year: sum("budget_year"),
        distributed_month: sum("distributed_month"),
        distributed_year: sum("distributed_year"),
        no_score_colleges: rows.filter(r => +r.no_score_count > 0).length
      };
    },
    budgetUsePct() {
      return this.totals.budget_year
        ? (this.totals.distributed_year / this.totals.budget_year) * 100
        : 0;
    },
    kpis() {
      const t = this.totals;
      return [
        { label: "สถานศึกษา", value: this.fmt(t.processed), unit: `จาก ${this.fmt(this.summary.expected_colleges)} แห่ง`, color: "#455a64" },
        { label: "พนักงานราชการ", value: this.fmt(t.personnel_count), unit: `มีสิทธิ์ ${this.fmt(t.eligible_count)} คน`, color: "#1565c0" },
        { label: "ฐานค่าตอบแทนรวม", value: this.fmt(t.total_salary), unit: "บาท/เดือน", color: "#2e7d32" },
        { label: `วงเงิน ${this.currentRun.budget_pct}% ที่ใช้ได้`, value: this.fmt(t.budget_year), unit: "บาท/ปี", color: "#ef6c00" },
        { label: "ยอดเลื่อนจริง", value: this.fmt(t.distributed_month), unit: "บาท/เดือน", color: "#6a1b9a" },
        { label: "ยอดเลื่อนจริง", value: this.fmt(t.distributed_year), unit: "บาท/ปี (×12)", color: "#6a1b9a" },
        { label: "วงเงินคงเหลือ", value: this.fmt(t.budget_year - t.distributed_year), unit: "บาท/ปี", color: "#00838f" },
        { label: "ค่าตอบแทนใหม่รวม", value: this.fmt(t.total_salary + t.distributed_month), unit: "บาท/เดือน", color: "#37474f" }
      ];
    },
    quality() {
      const t = this.totals;
      return [
        { label: "ไม่มีผลประเมิน", value: t.no_score_count, hint: `${this.fmt(t.no_score_colleges)} แห่ง`, color: "error--text", bg: "red lighten-5" },
        { label: "ผลประเมินยังไม่อนุมัติ", value: t.unapproved_score_count, hint: "นับรวมในการคำนวณ", color: "warning--text", bg: "orange lighten-5" },
        { label: "ไม่มีข้อมูลการลา", value: t.no_attendance_count, hint: "ถือว่าไม่มีวันลา", color: "warning--text", bg: "orange lighten-5" },
        { label: "ข้อมูลค่าตอบแทนผิดปกติ", value: t.data_issue_count, hint: "0 หรือ ≥ ขั้นสูง", color: "error--text", bg: "red lighten-5" },
        { label: "ถึงขั้นสูงกลุ่มงาน", value: t.capped_count, hint: "ข้อ 1(4)", color: "info--text", bg: "blue lighten-5" },
        { label: "ตัดที่เพดาน 6%", value: t.pct_capped_count, hint: "ข้อ 1(2)", color: "info--text", bg: "blue lighten-5" }
      ];
    },
    byRegion() {
      const map = {};
      ((this.summary && this.summary.colleges) || []).forEach(c => {
        const key = c.region_name || "ไม่ระบุภาค";
        const r = (map[key] = map[key] || {
          region: key, order: +c.region_ID || 99, colleges: 0, personnel_count: 0,
          eligible_count: 0, total_salary: 0, budget_year: 0, distributed_year: 0
        });
        r.colleges++;
        ["personnel_count", "eligible_count", "total_salary", "budget_year", "distributed_year"]
          .forEach(k => (r[k] += +c[k] || 0));
      });
      return Object.values(map).sort((a, b) => a.order - b.order);
    }
  },

  mounted() {
    this.loadOverview();
  },

  methods: {
    fmt(v) {
      if (v === null || v === undefined || isNaN(v)) return "-";
      return Number(v).toLocaleString("th-TH", { maximumFractionDigits: 0 });
    },
    fmt2(v) {
      if (v === null || v === undefined || isNaN(v)) return "-";
      return Number(v).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    certText(s) {
      return { submitted: "รอตรวจ", accepted: "รับรองแล้ว", rejected: "ส่งกลับแก้ไข" }[s] || "ยังไม่ส่ง";
    },
    certColor(s) {
      return { submitted: "info", accepted: "success", rejected: "error" }[s] || "grey";
    },
    openReview(college) {
      if (!college.cert_status) return;
      this.reviewCollege = college;
      this.reviewNote = "";
      this.reviewDocCode = "";
      this.reviewDialog = true;
      this.api({ action: "doc_code", run_id: this.currentRun.run_id, college_code: college.college_code })
        .then(r => (this.reviewDocCode = r.doc_code))
        .catch(() => (this.reviewDocCode = "-"));
    },
    async openOverride(item) {
      this.overrideRow = item;
      this.overrideForm = {
        pct: item.override_pct !== null ? +item.override_pct : +Number(item.actual_pct).toFixed(3),
        reason: item.override_reason || ""
      };
      this.overrideLog = [];
      this.overrideDialog = true;
      try {
        this.overrideLog = (await this.api({ action: "override_log", run_id: this.currentRun.run_id, id_card: item.id_card })).log;
      } catch (e) {
        // ไม่มีประวัติ — แสดงฟอร์มต่อได้
      }
    },
    async saveOverride(clear) {
      this.overrideSaving = true;
      try {
        const r = await this.api({
          action: clear ? "override_clear" : "override_set",
          run_id: this.currentRun.run_id,
          id_card: this.overrideRow.id_card,
          pct: this.overrideForm.pct,
          reason: this.overrideForm.reason
        });
        // ประมวลผลหน่วยงานนั้นใหม่ทันที (ผู้อื่นใช้อัตรากลางจากวงเงินที่เหลือ)
        const college = this.colleges.find(c => c.college_code === r.college_code);
        if (college) {
          await this.processOne(college);
          if (college.state === "error") throw new Error("ประมวลผลใหม่ไม่สำเร็จ: " + college.process_message);
          await this.loadSummary();
          await this.openCollegeDetail(this.colleges.find(c => c.college_code === r.college_code) || college);
        }
        this.overrideDialog = false;
        this.notify(clear ? "ยกเลิกการกำหนดร้อยละ และประมวลผลหน่วยงานใหม่แล้ว" : "กำหนดร้อยละรายคน และประมวลผลหน่วยงานใหม่แล้ว");
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.overrideSaving = false;
      }
    },
    async openTransfers() {
      this.transferDialog = true;
      await this.loadTransfers();
    },
    async loadTransfers() {
      try {
        const d = await this.api({ action: "transfer_list", comp_year: this.compYear });
        this.transferData = { detected: d.detected, candidates: d.candidates };
      } catch (e) {
        this.notify(e.message, "error");
      }
    },
    async markTransfer(t) {
      await this.changeTransfer(t, "transfer_mark", this.transferReason[t.id_card] || "");
    },
    async unmarkTransfer(t) {
      if (!confirm(`ยกเลิก "มีผล 1 ต.ค." ของ ${t.full_name}? ระบบจะคำนวณที่สถานศึกษาปัจจุบันแทน`)) return;
      await this.changeTransfer(t, "transfer_unmark", "");
    },
    // บันทึก/ยกเลิกวันที่มีผล แล้วประมวลผลทั้งสถานศึกษาเดิมและใหม่ใหม่ (ถ้ารอบที่เปิดอยู่เป็นรอบร่างของปีเดียวกัน)
    async changeTransfer(t, action, reason) {
      this.transferBusy = t.id_card;
      try {
        const r = await this.api({ action, comp_year: this.compYear, id_card: t.id_card, from: t.from, to: t.to, reason });
        await this.loadTransfers();
        const run = this.currentRun;
        if (run && run.run_status === "draft" && +run.comp_year === +this.compYear) {
          await this.loadSummary();
          const affected = this.colleges.filter(c => r.colleges.includes(c.college_code));
          await this.processColleges(affected);
          await this.loadSummary();
          this.notify(`บันทึกแล้ว และประมวลผลใหม่ ${affected.length} สถานศึกษา`);
        } else {
          this.notify("บันทึกแล้ว — มีผลเมื่อประมวลผลรอบของปีนี้");
        }
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.transferBusy = null;
      }
    },
    async openHolidays() {
      this.holidayDialog = true;
      await this.loadHolidays();
    },
    async loadHolidays() {
      try {
        this.holidays = (await this.api({ action: "holiday_list", comp_year: this.compYear })).holidays;
      } catch (e) {
        this.notify(e.message, "error");
      }
    },
    async saveHoliday() {
      try {
        await this.api({ action: "holiday_save", ...this.holidayForm });
        this.holidayForm = { holiday_date: "", holiday_name: "" };
        await this.loadHolidays();
      } catch (e) {
        this.notify(e.message, "error");
      }
    },
    async deleteHoliday(h) {
      if (!confirm(`ลบวันหยุด ${h.holiday_date} ${h.holiday_name}?`)) return;
      try {
        await this.api({ action: "holiday_delete", holiday_date: h.holiday_date });
        await this.loadHolidays();
      } catch (e) {
        this.notify(e.message, "error");
      }
    },
    // เอกสาร PDF (mPDF มีตราครุฑ) — เปิดในแท็บใหม่
    async openPdf(type, collegeCode = "") {
      this.pdfLoading = type;
      const win = window.open("", "_blank"); // เปิดก่อน await กันเบราว์เซอร์บล็อกป๊อปอัป
      try {
        const res = await this.$http.post(
          "govem_comp_pdf.php",
          { ApiKey: this.ApiKey, type, run_id: this.currentRun.run_id, college_code: collegeCode },
          { responseType: "blob" }
        );
        if (!(res.headers["content-type"] || "").includes("pdf")) {
          throw new Error(JSON.parse(await res.data.text()).message);
        }
        const url = URL.createObjectURL(res.data);
        if (win) win.location.href = url;
        else window.open(url, "_blank");
      } catch (e) {
        if (win) win.close();
        let msg = e.message;
        if (e.response && e.response.data instanceof Blob) {
          try {
            msg = JSON.parse(await e.response.data.text()).message;
          } catch (x) {
            // ใช้ข้อความเดิม
          }
        }
        this.notify("สร้างเอกสารไม่สำเร็จ: " + msg, "error");
      } finally {
        this.pdfLoading = null;
      }
    },
    async runAudit() {
      this.auditing = true;
      try {
        this.auditResult = await this.api({ action: "audit_run", run_id: this.currentRun.run_id });
        this.auditDialog = true;
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.auditing = false;
      }
    },
    openTrace() {
      this.traceResults = null;
      this.traceDialog = true;
    },
    async runTrace() {
      if ((this.traceQ || "").trim().length < 2) return;
      this.tracing = true;
      try {
        this.traceResults = (await this.api({ action: "trace", run_id: this.currentRun.run_id, q: this.traceQ.trim() })).results;
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.tracing = false;
      }
    },
    baseStatusText(s) {
      return {
        active: "ปฏิบัติงาน ณ 1 ต.ค.",
        retired: "เกษียณอายุ 30 ก.ย. (นับวงเงิน ไม่ได้เลื่อน)",
        resigned: "ลาออก/พ้นสภาพก่อน 1 ต.ค. (นับวงเงิน ไม่ได้เลื่อน)"
      }[s] || s || "-";
    },
    async openCertFile(college) {
      this.downloading = true;
      try {
        const res = await this.$http.post(
          API,
          { ApiKey: this.ApiKey, action: "download_cert", run_id: this.currentRun.run_id, college_code: college.college_code },
          { responseType: "blob" }
        );
        if ((res.headers["content-type"] || "").includes("application/json")) {
          throw new Error(JSON.parse(await res.data.text()).message);
        }
        window.open(URL.createObjectURL(res.data), "_blank");
      } catch (e) {
        this.notify("เปิดไฟล์ไม่สำเร็จ: " + e.message, "error");
      } finally {
        this.downloading = false;
      }
    },
    async openApply() {
      this.applyPreview = null;
      this.applyConfirm = false;
      this.applyDialog = true;
      try {
        this.applyPreview = await this.api({ action: "apply_preview", run_id: this.currentRun.run_id });
      } catch (e) {
        this.applyDialog = false;
        this.notify(e.message, "error");
      }
    },
    async confirmApply() {
      this.applying = true;
      try {
        const r = await this.api({ action: "apply_salary", run_id: this.currentRun.run_id });
        this.currentRun = r.run;
        this.applyDialog = false;
        this.notify(`บันทึกลงทะเบียนประวัติแล้ว ${this.fmt(r.applied)} คน` + (r.conflicts.length ? ` (ขัดแย้ง ${r.conflicts.length} คน ไม่บันทึก)` : ""),
          r.conflicts.length ? "warning" : "success");
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.applying = false;
      }
    },
    async revertApply() {
      if (!confirm("ยกเลิกการบันทึก? ระบบจะคืนค่าตอบแทนเดิมให้ทุกคนที่ยังไม่ถูกแก้ไขหลังบันทึก และลบประวัติค่าตอบแทนที่ระบบเพิ่ม")) return;
      this.applying = true;
      try {
        const r = await this.api({ action: "revert_salary", run_id: this.currentRun.run_id });
        this.currentRun = r.run;
        this.notify(`คืนค่าตอบแทนเดิมแล้ว ${this.fmt(r.reverted)} คน` + (r.kept.length ? ` (${r.kept.length} คนถูกแก้ไขภายหลัง ไม่คืนค่า)` : ""),
          r.kept.length ? "warning" : "info");
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.applying = false;
      }
    },

    async submitReview(decision) {
      this.reviewing = true;
      try {
        await this.api({
          action: "review_cert",
          run_id: this.currentRun.run_id,
          college_code: this.reviewCollege.college_code,
          decision,
          review_note: this.reviewNote
        });
        this.reviewDialog = false;
        await this.loadSummary();
        this.notify(decision === "accepted" ? "รับรองแบบรายงานแล้ว" : "ส่งกลับให้สถานศึกษาแก้ไขแล้ว", decision === "accepted" ? "success" : "info");
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.reviewing = false;
      }
    },
    notify(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
    roundStat(r) {
      const s = this.overview && this.overview.rounds.find(x => +x.assessment_round === r);
      return s ? { total: +s.total, approved: +s.approved, colleges: +s.colleges } : { total: 0, approved: 0, colleges: 0 };
    },
    parseNotes(v) {
      try {
        return v ? JSON.parse(v) : [];
      } catch (e) {
        return [];
      }
    },
    assessRef(item) {
      if (!item.assessment_ref) return null;
      try {
        return JSON.parse(item.assessment_ref);
      } catch (e) {
        return null;
      }
    },
    // ที่มาผลประเมินรายรอบ (รูปแบบใหม่มี rounds[]; รอบประมวลผลเก่ามีรอบเดียวที่ระดับบน)
    refRounds(item) {
      const ref = this.assessRef(item);
      if (!ref) return [];
      return ref.rounds && ref.rounds.length ? ref.rounds : [{ ...ref, round: item.score_round }];
    },
    scoreSourceText,
    methodText(v) {
      return v === "legacy" ? "สูตรเดิมของสถานศึกษา" : "ร้อยละร่วมตามคะแนน";
    },
    openSimulator(code) {
      this.simCollege = code;
      this.simDialog = true;
    },
    roundModeText(v) {
      return roundModeLabel(v);
    },
    salarySourceText(v) {
      return v === "ge" ? "ฐาน: กรอบอัตรากำลัง" : "ฐาน: ทะเบียนประวัติ";
    },
    stateText(s) {
      return { pending: "รอประมวลผล", processing: "กำลังประมวลผล", ok: "เสร็จ", error: "ผิดพลาด" }[s] || s;
    },
    stateColor(s) {
      return { pending: "grey", processing: "info", ok: "success", error: "error" }[s] || "grey";
    },

    async api(payload) {
      try {
        const res = await this.$http.post(API, { ApiKey: this.ApiKey, ...payload });
        if (!res.data || res.data.status !== true) {
          throw new Error((res.data && res.data.message) || "ไม่ได้รับข้อมูลจากเซิร์ฟเวอร์");
        }
        return res.data;
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.message;
        throw new Error(msg || err.message);
      }
    },

    async loadOverview() {
      this.currentRun = null;
      this.summary = null;
      this.colleges = [];
      try {
        const data = await this.api({ action: "overview", comp_year: this.compYear });
        this.overview = data;
        this.runs = data.runs;
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    async createRun() {
      this.busy = true;
      try {
        const data = await this.api({ action: "create_run", comp_year: this.compYear, ...this.newRun });
        this.newRun.run_note = "";
        await this.loadOverview();
        await this.selectRun(data.run);
        this.notify(`สร้างรอบ #${data.run.run_id} แล้ว — กด "ประมวลผลทุกสถานศึกษา" เพื่อเริ่ม`);
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = false;
      }
    },

    async deleteRun(run) {
      if (!confirm(`ลบรอบ #${run.run_id} และผลการประมวลผลทั้งหมดของรอบนี้?`)) return;
      try {
        await this.api({ action: "delete_run", run_id: run.run_id });
        await this.loadOverview();
        this.notify(`ลบรอบ #${run.run_id} แล้ว`, "info");
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    async selectRun(run) {
      try {
        const data = await this.api({ action: "colleges", run_id: run.run_id });
        this.currentRun = data.run;
        this.colleges = data.colleges.map(c => ({
          no_score_count: 0,
          data_issue_count: 0,
          cert_status: null,
          ...c,
          state: c.process_status ? (c.process_status === "error" ? "error" : "ok") : "pending"
        }));
        await this.loadSummary();
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    async loadSummary() {
      if (!this.currentRun) return;
      try {
        const data = await this.api({ action: "summary", run_id: this.currentRun.run_id });
        this.summary = data;
        this.currentRun = data.run;
        // ซิงก์ตัวเลขในคิวกับผลที่บันทึกจริง
        const byCode = {};
        data.colleges.forEach(c => (byCode[c.college_code] = c));
        this.colleges.forEach(c => {
          const s = byCode[c.college_code];
          if (s) Object.assign(c, s, { state: s.process_status === "error" ? "error" : "ok" });
        });
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    // ประมวลผลตามคิว ทีละ CONCURRENCY แห่ง
    async processColleges(list) {
      if (this.isFinal || this.processing || !list.length) return;
      this.processing = true;
      this.stopRequested = false;
      const queue = list.slice();
      list.forEach(c => {
        c.state = "pending";
        c.process_message = "";
      });
      const worker = async () => {
        while (queue.length && !this.stopRequested) {
          await this.processOne(queue.shift());
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));
      this.processing = false;
      await this.loadSummary();
      const failed = list.filter(c => c.state === "error").length;
      if (this.stopRequested) this.notify("หยุดการประมวลผลแล้ว", "warning");
      else if (failed) this.notify(`ประมวลผลเสร็จ ผิดพลาด ${failed} แห่ง — กด "เฉพาะที่ค้าง/ผิดพลาด" เพื่อลองใหม่`, "warning");
      else this.notify(`ประมวลผลเสร็จ ${list.length} แห่ง`);
    },

    async processOne(college) {
      const run = this.currentRun;
      college.state = "processing";
      try {
        const data = await this.api({ action: "college_data", run_id: run.run_id, college_code: college.college_code });
        const params = {
          budgetPct: +run.budget_pct,
          maxPct: +run.max_pct,
          roundMode: run.round_mode,
          minScore: +run.min_score,
          minMonths: +run.min_months,
          compYear: +run.comp_year,
          holidays: data.holidays || [],
          // ร้อยละรายคนที่ส่วนกลางกำหนด (ตาราง govem_comp_override) — เซิร์ฟเวอร์ตรวจซ้ำตอนบันทึก
          overrides: (data.overrides || []).reduce((m, o) => ({ ...m, [String(o.id_card)]: { pct: +o.pct, reason: o.reason } }), {})
        };
        const { records, summary } =
          run.calc_method === "legacy" ? calculateCollege(data.employees, params) : calculateCollegeV2(data.employees, params);
        const saved = await this.api({
          action: "save_college",
          run_id: run.run_id,
          college_code: college.college_code,
          details: records,
          summary
        });
        Object.assign(college, {
          state: "ok",
          personnel_count: saved.personnel,
          eligible_count: saved.eligible,
          budget_year: saved.budget_year,
          distributed_year: saved.distributed_year,
          process_message: saved.eligible === 0 ? "ไม่มีผู้มีสิทธิ์รับการเลื่อน" : ""
        });
      } catch (e) {
        college.state = "error";
        college.process_message = e.message;
      }
    },

    async finalizeRun() {
      if (!confirm("ยืนยันผลรอบนี้? หลังยืนยันจะไม่สามารถประมวลผลใหม่ได้จนกว่าจะยกเลิกการยืนยัน")) return;
      try {
        await this.api({ action: "finalize", run_id: this.currentRun.run_id });
        await this.loadSummary();
        this.runs = (await this.api({ action: "overview", comp_year: this.compYear })).runs;
        this.notify("ยืนยันผลเรียบร้อย");
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    async reopenRun() {
      if (!confirm("ยกเลิกการยืนยันผลรอบนี้เพื่อประมวลผลใหม่?")) return;
      try {
        await this.api({ action: "reopen", run_id: this.currentRun.run_id });
        await this.loadSummary();
        this.runs = (await this.api({ action: "overview", comp_year: this.compYear })).runs;
        this.notify("ยกเลิกการยืนยันแล้ว", "info");
      } catch (e) {
        this.notify(e.message, "error");
      }
    },

    async openCollegeDetail(college) {
      this.detailCollege = college;
      this.detailRows = [];
      this.detailDialog = true;
      this.detailLoading = true;
      try {
        const data = await this.api({
          action: "details",
          run_id: this.currentRun.run_id,
          college_code: college.college_code
        });
        this.detailRows = data.details;
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.detailLoading = false;
      }
    },

    reportTitle() {
      const r = this.currentRun;
      return {
        title: "สรุปการเลื่อนค่าตอบแทนพนักงานราชการ สังกัดสำนักงานคณะกรรมการการอาชีวศึกษา",
        sub: `ผลการประเมินปีงบประมาณ ${r.comp_year} (${this.roundModeText(r.round_mode)}) · มีผล 1 ตุลาคม ${r.comp_year}` +
          ` · วงเงินไม่เกิน ${r.budget_pct}% · ${this.salarySourceText(r.salary_source)}` +
          ` · รอบประมวลผล #${r.run_id} ${r.run_status === "final" ? "(ยืนยันผล " + r.finalized_at + ")" : "(ฉบับร่าง)"}`
      };
    },

    async exportExcel() {
      this.exporting = true;
      try {
        const data = await this.api({ action: "details", run_id: this.currentRun.run_id });
        const t = this.totals;
        const head = this.reportTitle();
        const wb = XLSX.utils.book_new();

        const summaryRows = [
          [head.title], [head.sub], [],
          ["รายการ", "จำนวน", "หน่วย"],
          ["สถานศึกษาที่ประมวลผล", t.processed, "แห่ง"],
          ["พนักงานราชการทั้งหมด", t.personnel_count, "คน"],
          ["ผู้มีสิทธิ์รับการเลื่อน", t.eligible_count, "คน"],
          ["ฐานค่าตอบแทนรวม", t.total_salary, "บาท/เดือน"],
          [`วงเงิน ${this.currentRun.budget_pct}% ที่ใช้ได้`, t.budget_year, "บาท/ปี"],
          ["ยอดเลื่อนค่าตอบแทนจริง", t.distributed_month, "บาท/เดือน"],
          ["ยอดเลื่อนค่าตอบแทนจริง", t.distributed_year, "บาท/ปี"],
          ["วงเงินคงเหลือ", t.budget_year - t.distributed_year, "บาท/ปี"],
          ["ไม่มีผลประเมิน", t.no_score_count, "คน"],
          ["ข้อมูลค่าตอบแทนผิดปกติ", t.data_issue_count, "คน"],
          [], ["ภาค", "สถานศึกษา", "พนักงาน", "มีสิทธิ์", "ฐาน (บาท/เดือน)", "วงเงิน (บาท/ปี)", "เลื่อนจริง (บาท/ปี)"],
          ...this.byRegion.map(r => [r.region, r.colleges, r.personnel_count, r.eligible_count, r.total_salary, r.budget_year, r.distributed_year])
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), "สรุป");

        const collegeRows = this.summary.colleges.map((c, i) => ({
          ลำดับ: i + 1,
          รหัส: c.college_code,
          สถานศึกษา: c.college_name,
          จังหวัด: c.province_name,
          ภาค: c.region_name,
          พนักงาน: +c.personnel_count,
          มีสิทธิ์: +c.eligible_count,
          ไม่มีผลประเมิน: +c.no_score_count,
          "ฐานค่าตอบแทน (บาท/เดือน)": +c.total_salary,
          [`วงเงิน ${this.currentRun.budget_pct}% (บาท/ปี)`]: +c.budget_year,
          "เลื่อนจริง (บาท/เดือน)": +c.distributed_month,
          "เลื่อนจริง (บาท/ปี)": +c.distributed_year,
          "คงเหลือ (บาท/ปี)": +c.budget_year - +c.distributed_year,
          "สถานศึกษาอนุมัติเองแล้ว (ระบบเดิม)": +c.college_approved ? "ใช่" : "",
          "แบบรับรอง ผอ.": this.certText(c.cert_status),
          "ผู้ลงนาม": c.cert_director_name || "",
          "แนบเมื่อ": c.cert_uploaded_at || ""
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(collegeRows), "รายสถานศึกษา");

        const detailRows = data.details.map((d, i) => ({
          ลำดับ: i + 1,
          รหัสสถานศึกษา: d.college_code,
          สถานศึกษา: d.college_name,
          เลขบัตรประชาชน: d.id_card,
          "ชื่อ-สกุล": d.full_name,
          ตำแหน่ง: d.name_postion,
          กลุ่มงาน: d.name_type_group,
          ค่าตอบแทนเดิม: +d.salary_s,
          "ผลประเมินรอบ 1": d.score_r1 !== null && d.score_r1 !== undefined ? +d.score_r1 : "",
          "ผลประเมินรอบ 2": d.score_r2 !== null && d.score_r2 !== undefined ? +d.score_r2 : "",
          "ฐานคะแนน (เฉลี่ย)": +d.performance_score,
          รอบที่ใช้: d.score_round || "",
          "อายุงาน (เดือน)": d.service_months !== null && d.service_months !== undefined ? +d.service_months : "",
          สถานะผลประเมิน: d.assessment_status || "",
          สิทธิ์: +d.eligible ? "มีสิทธิ์" : "ไม่มีสิทธิ์",
          หมายเหตุ: d.ineligible_reason || "",
          "% รายบุคคล": +d.individual_pct,
          "% บริหารวงเงิน": +d.management_pct,
          "% เลื่อนจริงก่อนปัดเศษ": +d.actual_pct_raw,
          "เลื่อนจริงก่อนปัดเศษ (บาท)": +d.actual_increase_raw,
          "% เลื่อนจริง": +d.actual_pct,
          "เลื่อนจริง (บาท/เดือน)": +d.final_rounded,
          ค่าตอบแทนใหม่: +d.new_salary,
          ถึงขั้นสูง: +d.capped ? "ใช่" : "",
          ตัดที่เพดาน6: +d.pct_capped ? "ใช่" : ""
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), "รายบุคคล");

        XLSX.writeFile(wb, `เลื่อนค่าตอบแทนพนักงานราชการ_${this.currentRun.comp_year}_รอบ${this.currentRun.run_id}.xlsx`);
      } catch (e) {
        this.notify("ส่งออกไม่สำเร็จ: " + e.message, "error");
      } finally {
        this.exporting = false;
      }
    },

    // รายงานสำหรับพิมพ์/บันทึก PDF เสนอสำนักงบประมาณ (สรุปรายภาค + รายสถานศึกษา)
    printReport() {
      const esc = s => String(s === null || s === undefined ? "" : s)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const t = this.totals;
      const head = this.reportTitle();
      const n = v => this.fmt(v);
      const regionRows = this.byRegion.map(r =>
        `<tr><td>${esc(r.region)}</td><td>${n(r.colleges)}</td><td>${n(r.personnel_count)}</td><td>${n(r.eligible_count)}</td>` +
        `<td>${n(r.total_salary)}</td><td>${n(r.budget_year)}</td><td>${n(r.distributed_year)}</td></tr>`).join("");
      const collegeRows = this.summary.colleges.map((c, i) =>
        `<tr><td>${i + 1}</td><td class="l">${esc(c.college_name)}</td><td class="l">${esc(c.province_name)}</td>` +
        `<td>${n(c.personnel_count)}</td><td>${n(c.eligible_count)}</td><td>${n(c.total_salary)}</td>` +
        `<td>${n(c.budget_year)}</td><td>${n(c.distributed_month)}</td><td>${n(c.distributed_year)}</td></tr>`).join("");
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(head.title)}</title>
        <style>
          body{font-family:"TH Sarabun New","Sarabun",sans-serif;font-size:15px;margin:24px;color:#000}
          h2{text-align:center;margin:0 0 4px;font-size:20px} .sub{text-align:center;margin-bottom:12px}
          table{border-collapse:collapse;width:100%;margin-bottom:16px} th,td{border:1px solid #444;padding:2px 6px;text-align:right}
          th{background:#eee;text-align:center} td.l{text-align:left} tfoot td{font-weight:bold;background:#f5f5f5}
          .sign{margin-top:40px;width:45%;margin-left:auto;text-align:center;line-height:2}
          @media print{thead{display:table-header-group}}
        </style></head><body>
        <h2>${esc(head.title)}</h2><div class="sub">${esc(head.sub)}</div>
        <table><thead><tr><th>ภาค</th><th>สถานศึกษา</th><th>พนักงาน</th><th>มีสิทธิ์</th><th>ฐานค่าตอบแทน (บาท/เดือน)</th>
          <th>วงเงิน ${esc(this.currentRun.budget_pct)}% (บาท/ปี)</th><th>เลื่อนจริง (บาท/ปี)</th></tr></thead>
          <tbody>${regionRows}</tbody>
          <tfoot><tr><td class="l">รวมทั้งสิ้น</td><td>${n(t.processed)}</td><td>${n(t.personnel_count)}</td><td>${n(t.eligible_count)}</td>
          <td>${n(t.total_salary)}</td><td>${n(t.budget_year)}</td><td>${n(t.distributed_year)}</td></tr></tfoot></table>
        <table><thead><tr><th>ที่</th><th>สถานศึกษา</th><th>จังหวัด</th><th>พนักงาน</th><th>มีสิทธิ์</th><th>ฐาน (บาท/เดือน)</th>
          <th>วงเงิน (บาท/ปี)</th><th>เลื่อนจริง (บาท/เดือน)</th><th>เลื่อนจริง (บาท/ปี)</th></tr></thead>
          <tbody>${collegeRows}</tbody>
          <tfoot><tr><td></td><td class="l">รวม</td><td></td><td>${n(t.personnel_count)}</td><td>${n(t.eligible_count)}</td>
          <td>${n(t.total_salary)}</td><td>${n(t.budget_year)}</td><td>${n(t.distributed_month)}</td><td>${n(t.distributed_year)}</td></tr></tfoot></table>
        <div>หมายเหตุ: ไม่มีผลการประเมิน ${n(t.no_score_count)} คน · ข้อมูลค่าตอบแทนผิดปกติ ${n(t.data_issue_count)} คน ·
          ถึงขั้นสูงกลุ่มงาน ${n(t.capped_count)} คน · ตัดที่เพดาน 6% ${n(t.pct_capped_count)} คน</div>
        <div class="sign">(ลงชื่อ)......................................................<br>(......................................................)<br>ผู้จัดทำรายงาน</div>
        </body></html>`;
      const w = window.open("", "_blank");
      if (!w) {
        this.notify("เบราว์เซอร์บล็อกหน้าต่างใหม่ กรุณาอนุญาต pop-up", "warning");
        return;
      }
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    }
  }
};
</script>

<style scoped>
.kpi {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  border-left: 4px solid #455a64;
}
.kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: #777;
}
.kpi-value {
  font-size: 20px;
  font-weight: bold;
  margin: 4px 0;
}
.kpi-unit {
  font-size: 11px;
  color: #999;
}
.report-table >>> th,
.report-table >>> td {
  font-size: 12px !important;
}
</style>
